import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, from, of, tap } from 'rxjs';
import { WebEnvironment } from '@aaa/emember/types';
import { WindowRefService } from '../modules/share/services/window-ref.service';
import { environment } from '../../environments/environment';
import {
  Auth,
  getIdTokenResult,
  setPersistence,
  signInWithCustomToken,
  user,
  browserSessionPersistence,
  signInWithPopup,
  signOut,
  GoogleAuthProvider,
} from '@angular/fire/auth';
import { IdTokenResult, ParsedToken } from 'firebase/auth';
import { switchMap } from 'rxjs/operators';

interface ParsedTokenClaims extends ParsedToken {
  roles?: string[];
  memberNumber?: string;
}

interface TokenResult extends IdTokenResult {
  claims: ParsedTokenClaims;
}

@Injectable({ providedIn: 'root' })
export class JwtAuthService {
  auth = inject(Auth);
  windowRefService = inject(WindowRefService);
  signedIn = false;
  afAuthIdTokenResult$ = new BehaviorSubject<TokenResult | null>(null);

  constructor() {
    /**
     * "claims.roles" is the only data being used from globalState.afAuthIdTokenResult
     * "claims.token" may be used soon for server-side user validation
     * "claims.token" os a client token
     * "windowMetaData.user.token" is a server token, and is used for user login, then we get the client token from idTokenResult.claims
     * "claims.roles" and "claims.memberNumber" are used in this.idTokenResult$ side effect
     */
    user(this.auth)
      .pipe(
        switchMap((u) => (u ? from(getIdTokenResult(u).then((u) => u as TokenResult)) : of(null))),
        tap(async (idTokenResult) => {
          this.afAuthIdTokenResult$.next(idTokenResult);

          if (!idTokenResult && !this.signedIn) {
            await this.signInUser();
          }

          if (idTokenResult) {
            this.signedIn = true;

            // const webEnv = this.globalState.get('windowMetaData', 'webEnv')?.toUpperCase();
            // const environment = this.globalState.get('environment');
            const webEnv = this.windowRefService.nativeWindow.metaData?.webEnv?.toUpperCase();

            if (!environment.emulator && !environment.ngServe && webEnv !== WebEnvironment.APP_TEST) {
              /**
               * Log out the current firebase user if their roles or member number
               * are different from the data from our windowMetaData object.
               */
              const drupalUser = this.windowRefService.nativeWindow.metaData?.user;
              const tokenNumber = idTokenResult.claims.memberNumber || '';
              const metaNumber = drupalUser?.memberNumber || '';

              const array1 = idTokenResult.claims?.roles as string[];
              const array2: string[] = drupalUser?.roles;

              let arraysAreEqual = false;

              if (array1 && array1.length === array2?.length) {
                const sortedArray1 = array1.sort();
                const sortedArray2 = array2.sort();

                if (sortedArray1.every((value: string, index: number) => value === sortedArray2[index])) {
                  arraysAreEqual = true;
                }
              }

              if (tokenNumber !== metaNumber || !arraysAreEqual) {
                if (tokenNumber !== metaNumber) {
                  console.log(idTokenResult);
                  console.log(tokenNumber);
                  console.log(metaNumber);
                }
                if (!arraysAreEqual) {
                  console.log(array1);
                  console.log(array2);
                }
                this.signOutUser();
              }
            }
            /**
             * Do nothing if in emulator mode.
             * User already signed in with popup login and has an idTokenResult.
             */
          }
        }),
      )
      .subscribe(() => {
        console.log('login subscribe');
      });
  }

  async signOutUser() {
    try {
      await signOut(this.auth);

      this.signedIn = false;
    } catch (err) {
      console.error('Error:', err);
    }
  }

  async signInUser() {
    if (!this.signedIn) {
      const webEnv = this.windowRefService.nativeWindow.metaData?.webEnv?.toUpperCase();
      const isTestEnv = webEnv === WebEnvironment.APP_TEST;

      if (environment?.emulator || environment?.ngServe || isTestEnv) {
        try {
          const provider = new GoogleAuthProvider();

          await signInWithPopup(this.auth, provider);

          this.signedIn = true;
        } catch (err) {
          console.error('Error:', err);
        }
      }

      if (
        !environment?.emulator &&
        !environment?.ngServe &&
        !isTestEnv &&
        this.windowRefService.nativeWindow.metaData?.user?.token
      ) {
        try {
          // await setPersistence(this.auth, browserSessionPersistence);
          await setPersistence(this.auth, browserSessionPersistence);

          const token = this.windowRefService.nativeWindow.metaData?.user?.token;

          await signInWithCustomToken(this.auth, token);

          this.signedIn = true;
        } catch (err) {
          console.error('Error:', err);
        }
      }
    }
  }
}
